<template>
  <div class="flex flex-col justify-between flex-grow h-full gap-2">
    <div class="flex items-center content-center justify-center h-full">
      <div
        :id="`percentage-scroll-${measurement.id}`"
        class="flex w-full gap-4 pb-4 overflow-x-auto scrolling-touch max-w-72 snap-x-mandatory scrollbar-lg"
        dir="ltr"
        @scroll="onScroll"
      >
        <div
          v-for="(percentageBoxes, idx) in percentageBoxesPages"
          :key="`${measurement.id}-percentage-boxes-${idx + 1}`"
          :id="`${measurement.id}-percentage-boxes-${idx + 1}`"
          class="flex justify-center flex-shrink-0 w-full snap-center"
        >
          <div
            class="flex flex-wrap items-start content-center justify-center w-full transition-all duration-300"
            :class="[
              isCollapsed
                ? 'gap-2'
                : sessionStatus === 'draft'
                ? 'gap-2 2xl:gap-3'
                : 'gap-3'
            ]"
          >
            <!-- :class="{
              'gap-x-2 gap-y-2': !isCollapsed && sessionStatus === 'draft',
              'gap-x-3 gap-y-3': !isCollapsed && sessionStatus !== 'draft',
              'gap-x-2 gap-y-2': isCollapsed
            }" -->
            <div
              v-for="box in percentageBoxes"
              :key="box.key"
              class="flex items-center justify-center flex-shrink-0 transition-all duration-300 border rounded cursor-pointer"
              :class="{
                'h-7.5 w-7.5 xl:h-8 xl:w-8 2xl:h-10 2xl:w-10':
                  sessionStatus === 'draft',
                'h-10 w-10': sessionStatus !== 'draft',
                'cursor-wait':
                  percentageLoadingBox && percentageLoadingBox !== box.key,
                'pointer-events-none': sessionStatus !== 'ongoing',
                'border-slate-500 bg-pure-white': box.value === null,
                'border-grass-700 bg-grass-100': box.value === true,
                'border-tomato-700 bg-tomato-100': box.value === false
              }"
              @click="onChangePercentage(box)"
            >
              <BlIcon
                v-if="box.value === true"
                icon="check"
                color="success"
                class="w-10 h-10"
              />
              <BlIcon
                v-if="box.value === false"
                icon="close"
                color="error"
                class="w-6 h-6"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex-shrink-0 pb-3 space-y-2"
      :class="{ '-translate-y-2': isCollapsed }"
    >
      <div class="z-10 flex items-center justify-center h-3 gap-2">
        <div
          v-for="n in pageCount"
          :key="n"
          :class="{ 'bg-slate-700': n === page, 'bg-slate-400': n !== page }"
          class="w-3 h-3 transition-all duration-300 rounded-full cursor-pointer"
          @click="onChangePage(n)"
        ></div>
      </div>
      <div
        v-if="!isCollapsed"
        class="z-10 flex items-center justify-center gap-2 text-center"
      >
        <div>
          <BlTextBody2 text="Goal:" />
          <BlTextBody2 :text="`${measurement.target.goal}%`" />
        </div>
        <div>
          <BlTextBody2 text="Score:" />
          <BlTextBody2 :text="`${percentageScore.toFixed(0)}%`" />
        </div>
      </div>
      <div
        v-if="sessionStatus === 'draft' && measurement.target.probing_enable"
        class="z-10 flex items-center justify-between w-full px-4 py-2 rounded-full bg-pure-white"
      >
        <div>
          <BlTextSubtitle3 text="Set as probing" class="text-lime-700" />
        </div>
        <div v-if="measurement.type" class="flex gap-1">
          <BlTextSubtitle3
            :text="measurement.type.includes('Probing') ? 'Yes' : 'No'"
            class="text-lime-700"
          />
          <BlInputToggle
            :checked="measurement.type.includes('Probing')"
            color="lime"
            :loading="switchLoading"
            @changed="onChangeProbing"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";

import BlTextTitle3 from "../../BlTextTitle3.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputToggle from "../../BlInputToggle.vue";

export default {
  name: "MeasurementCardPercentage",
  components: {
    BlTextTitle3,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlInputButton,
    BlInputToggle
  },
  props: {
    measurement: { type: Object, required: true },
    sessionStatus: { type: String, required: true },
    isCollapsed: { type: Boolean, default: false }
    // @toggle-updated
  },
  data() {
    return {
      page: 1,
      results: {},
      percentageLoadingBox: null,
      //
      switchLoading: false
    };
  },
  computed: {
    ...mapGetters(["viewMode"]),
    perPage() {
      return this.isCollapsed ? 5 : 20;
    },
    pageCount() {
      const boxes = Object.keys(this.results).length;
      return Math.ceil(boxes / this.perPage);
    },
    percentageBoxesPages() {
      const boxes = Object.keys(this.results).map(key => {
        return { key, value: this.results[key] };
      });
      const res = [];
      for (let idx = 1; idx <= this.pageCount; idx++) {
        const start = (idx - 1) * this.perPage;
        const end = idx * this.perPage;
        const arr = [...boxes.slice(start, end)];
        res.push(arr);
      }
      return res;
    },
    percentageScore() {
      const results = this.results;
      const trials = Object.values(results).length;
      const totalSuccess = Object.values(results).filter(i => i).length;
      return (totalSuccess / trials) * 100 || 0;
    }
  },
  watch: {
    percentageLoadingBox(val) {
      if (val === null) {
        this.$emit("toggle-updated", true);
      } else {
        this.$emit("toggle-updated", false);
      }
    },
    isCollapsed() {
      setTimeout(() => {
        const id = `${this.measurement.id}-percentage-boxes-${1}`;
        const boxes = document.getElementById(id);
        if (boxes) {
          boxes.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center"
          });
        }
      }, 300);
    }
  },
  mounted() {
    this.results = { ...this.measurement.results };
  },
  methods: {
    ...mapActions([
      "getMeasurements",
      "updateMeasurementResults",
      "updateMeasurement",
      "createMeasurement",
      "deleteMeasurement"
    ]),
    onScroll(e) {
      const offsetWidth = e.currentTarget.offsetWidth;
      const scrollLeft = e.currentTarget.scrollLeft;
      const current = Math.floor(scrollLeft / offsetWidth) + 1;
      this.page = current;
    },
    onChangePage(num) {
      const id = `${this.measurement.id}-percentage-boxes-${num}`;
      const boxes = document.getElementById(`${id}`);
      if (boxes) {
        boxes.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
      }
    },
    // ongoing
    onSavePercentage: debounce(async function(box) {
      let val = null;
      if (box.value === null) val = true;
      if (box.value === true) val = false;
      if (box.value === false) val = null;

      const params = {
        id: this.measurement.id,
        results: {}
      };
      params.results[box.key] = val;

      this.percentageLoadingBox = box.key;
      const { success, data } = await this.updateMeasurementResults(params);
      this.percentageLoadingBox = null;
      if (!success) {
        this.results = { ...this.measurement.results };
        return;
      }

      this.results = { ...data.results };
    }, 1000),
    async onChangePercentage(box) {
      if (this.sessionStatus !== "ongoing") return;
      if (this.percentageLoadingBox && this.percentageLoadingBox !== box.key) {
        return;
      }

      // change state
      let val = null;
      if (box.value === null) val = true;
      if (box.value === true) val = false;
      if (box.value === false) val = null;

      this.results[box.key] = val;

      // save state
      this.percentageLoadingBox = box.key;
      this.onSavePercentage(box);
    },
    // draft
    async onChangeProbing() {
      if (this.sessionStatus !== "draft") return;
      if (this.switchLoading) return;
      const checked = this.measurement.type.includes("Probing");
      const temp = {
        session_id: this.measurement.session_id,
        target_id: this.measurement.target_id,
        measurement_id: this.measurement.id,
        position: this.measurement.position,
        is_fixed: this.measurement.is_fixed
      };

      if (!checked) {
        const updateParams = {
          skip_action: true,
          id: temp.measurement_id,
          measurement: {
            visible: false
          }
        };

        this.switchLoading = true;
        const { success: successUpdate } = await this.updateMeasurement(
          updateParams
        );
        if (!successUpdate) {
          this.switchLoading = false;
          return;
        }

        const createParams = {
          id: temp.session_id,
          skip_action: true,
          target_id: temp.target_id,
          measurement: {
            type: "Measurement::Probing",
            position: temp.position,
            is_fixed: temp.is_fixed
          }
        };
        const { success: succesCreate } = await this.createMeasurement(
          createParams
        );
        if (!succesCreate) {
          this.switchLoading = false;
          return;
        }
      } else {
        const params = {
          id: temp.measurement_id,
          skip_action: true
        };

        this.switchLoading = true;
        const { success } = await this.deleteMeasurement(params);
        if (!success) {
          this.switchLoading = false;
          return;
        }
      }
      setTimeout(async () => {
        await this.getMeasurements({ id: temp.session_id });
        this.switchLoading = false;
      }, 300);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/sessions/card/Percentage.vue